import * as React from 'react';
import { useDriverProfile } from '../../hooks/useDriverProfile';
import {
  useAddVehicleNote,
  useDeleteVehicleNote,
  useGetVehicleNotes,
  useUpdateVehicleNote,
} from '../../hooks/useVehicleNotes';
import type { Adder } from '../shell/adder';
import { NotesSection } from '../shell/NotesSection';

/**
 * The vehicle's notes, a composer to add one, and the member's own to
 * change or delete. Only the writer may: the server answers anyone else
 * with a 404, so only their notes open.
 */
export const CallNotes = React.forwardRef<
  Adder,
  {
    vehicleId: string;
    /** May add notes; the server refuses anyone else. */
    editable?: boolean;
  }
>(function CallNotes({ vehicleId, editable = true }, ref) {
  const { data } = useGetVehicleNotes(vehicleId);
  const { data: profile } = useDriverProfile();
  const addNote = useAddVehicleNote();
  const updateNote = useUpdateVehicleNote();
  const deleteNote = useDeleteVehicleNote();

  return (
    <NotesSection
      ref={ref}
      notes={(data?.notes ?? []).map((note) => ({
        id: note.id,
        text: note.noteText,
        author: note.user?.name ?? 'Someone',
        createdAt: note.createTime,
        own: !!profile?.userId && note.userId === profile.userId,
      }))}
      editable={editable}
      saving={addNote.isPending || updateNote.isPending}
      deleting={deleteNote.isPending}
      onAdd={(noteText) =>
        addNote.mutateAsync({ vehicleId, noteData: { noteText } })
      }
      onChange={(noteId, noteText) =>
        updateNote.mutateAsync({ noteId, noteData: { noteText } })
      }
      onDelete={(noteId) => deleteNote.mutateAsync(noteId)}
    />
  );
});
